import type { Locale } from '../locale';

const en = {
  accent: 'Make your AI budget go further.',
  intro: 'Learn the model and context choices behind lower-cost agent workflows. See the difference in dollars, then practise on real tasks and measure your own results.',
  calculate: 'See what you could save',
  example: 'A smaller AI bill, in dollars',
  before: 'Example monthly API budget',
  after: 'At the model-routing benchmark ratio',
  difference: 'Illustrative monthly difference',
  basis: 'Based on our audited 50-task model-routing comparison. Normal-turn API-equivalent costs.',
  caveat: '49/50 compliant successes in each condition, with one task regression. This is an illustration, not a forecast or a course-completion savings promise.',
  adjust: 'Try your own budget below',
  credibility: 'Who built it',
  howTitle: 'Give this to your agent',
  /* The front door (docs/server-workspace.md, 6): one line for the visitor's
     own agent, which reads /start.md and connects to a workspace on our
     server. Nothing here downloads; the in-browser machine is the by-hand link. */
  barStart: 'Start with your agent',
  lineLabel: 'One line for Claude Code, Codex, Cursor or any agent that can add an MCP server',
  line: 'Read https://engineeros.dev/start and set me up.',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Select the line and copy it',
  next: 'Your agent connects to a workspace on our server and asks you to approve once in your browser. Then you choose a task on your desk and brief it.',
  byHand: 'Do it by hand in your browser',
  byHandNote: 'Downloads a small Linux machine into this tab, about 13 MB to start.',
  watch: 'Watch: why you steer agents, not swim',
  expect: 'In our trials the five free levels cost a Sonnet agent about $1 in all, paid from your own plan or API credits. Your saving is measured on your own runs, never estimated.',
};

export const efficiencyHeroCopy: Record<Locale, typeof en> = {
  en,
  ar: {
    accent: 'استفد أكثر من ميزانية الذكاء الاصطناعي.',
    intro: 'تعلّم اختيار النموذج والسياق لتقليل تكلفة عمل الوكلاء. شاهد الفرق بالدولار، ثم تدرّب في مهام حقيقية وقِس نتائجك أنت.',
    calculate: 'شاهد كم يمكنك أن توفّر', example: 'فاتورة أصغر، بالدولار',
    before: 'مثال لميزانية API الشهرية', after: 'بنسبة اختبار توجيه النماذج', difference: 'الفرق الشهري في هذا المثال',
    basis: 'بناءً على مقارنتنا المدقّقة لتوجيه النماذج في 50 مهمة. تكلفة الطلبات العادية بما يعادل أسعار API.',
    caveat: '49 نجاحًا ملتزمًا من 50 في كل حالة، مع تراجع مهمة واحدة. هذا مثال وليس تنبّؤًا أو وعدًا بتوفير بعد إنهاء الدورة.',
    adjust: 'جرّب ميزانيتك في الأسفل', credibility: 'من بناها',
    howTitle: 'أعطِ هذا لوكيلك',
    // English only for now (owner, 2026-10-29).
    barStart: en.barStart, lineLabel: en.lineLabel, line: en.line, copy: en.copy, copied: en.copied, copyFailed: en.copyFailed,
    next: en.next, byHand: en.byHand, byHandNote: en.byHandNote, watch: en.watch,
    expect: 'في تجاربنا كلّفت المستويات الخمسة المجانية وكيلَ Sonnet نحو دولار واحد في مجموعها، من اشتراكك في Claude أو رصيد API الخاص بك. توفيرك يُقاس من محاولاتك أنت، ولا يُقدَّر أبدًا.',
  },
  de: {
    accent: 'Mehr aus deinem KI-Budget machen.',
    intro: 'Lerne die Modell- und Kontextentscheidungen hinter günstigeren Agentenabläufen. Sieh den Unterschied in Dollar, übe an echten Aufgaben und miss deine eigenen Ergebnisse.',
    calculate: 'Mögliche Ersparnis ansehen', example: 'Eine kleinere KI-Rechnung, in Dollar',
    before: 'Beispiel für monatliches API-Budget', after: 'Mit dem Verhältnis des Modellrouting-Benchmarks', difference: 'Beispielhafter monatlicher Unterschied',
    basis: 'Auf Basis unseres geprüften Modellrouting-Vergleichs mit 50 Aufgaben. API-äquivalente Kosten normaler Turns.',
    caveat: 'Je 49/50 regelkonforme Erfolge, mit einer Aufgabenregression. Ein Beispiel, keine Prognose und kein Sparversprechen nach dem Kurs.',
    adjust: 'Unten dein Budget ausprobieren', credibility: 'Wer es gebaut hat',
    howTitle: 'Gib das deinem Agenten',
    // English only for now (owner, 2026-10-29).
    barStart: en.barStart, lineLabel: en.lineLabel, line: en.line, copy: en.copy, copied: en.copied, copyFailed: en.copyFailed,
    next: en.next, byHand: en.byHand, byHandNote: en.byHandNote, watch: en.watch,
    expect: 'In unseren Versuchen kosteten die fünf kostenlosen Level einen Sonnet-Agenten zusammen etwa 1 $, bezahlt aus deinem eigenen Claude-Abo oder API-Guthaben. Deine Ersparnis wird an deinen eigenen Läufen gemessen, nie geschätzt.',
  },
  ja: {
    accent: 'AI予算を、もっと有効に。',
    intro: 'コストを抑えるモデルとコンテキストの選び方を学びます。まず金額の差を確認し、実際のタスクで練習して自分の結果を計測しましょう。',
    calculate: '節約できる金額の例を見る', example: 'AIの支出を、金額で比べる',
    before: '月間API予算の例', after: 'モデル選択の実験比率で換算', difference: 'この例での月間差額',
    basis: '監査済みの50タスクのモデル選択比較に基づく、通常ターンのAPI相当コストです。',
    caveat: '両方49/50が規則を守って成功し、1タスクが悪化しました。これは例であり、予測や修了後の節約の約束ではありません。',
    adjust: '下で自分の予算を試す', credibility: '開発者について',
    howTitle: 'これをエージェントに渡す',
    // English only for now (owner, 2026-10-29).
    barStart: en.barStart, lineLabel: en.lineLabel, line: en.line, copy: en.copy, copied: en.copied, copyFailed: en.copyFailed,
    next: en.next, byHand: en.byHand, byHandNote: en.byHandNote, watch: en.watch,
    expect: '私たちの試行では、無料の5レベルを Sonnet エージェントで解いた費用は合計約1ドルでした。費用はご自身の Claude プランか API クレジットから支払われます。節約額は推定ではなく、あなた自身の実行で計測します。',
  },
};
